import React, { useEffect, useState } from 'react';
import { SelectAllCheckboxesView, SelectAllCheckboxesViewProps } from './SelectAllCheckboxes.view';
import { Field } from 'react-hook-form';

export type SelectAllCheckboxesControllerProps = Omit<SelectAllCheckboxesViewProps, 'onSetState' | 'checkboxState'> & {
  // extend your SelectAllCheckboxesViewProps
};

export type CheckboxStateType = true | false | 'indeterminate';
type FieldWithFields = Field & Record<number, Field>;

export const SelectAllCheckboxesController = (props: SelectAllCheckboxesControllerProps): JSX.Element => {
  const [checkboxState, setCheckboxState] = useState<CheckboxStateType>(false);
  // Put All your methods and logic here
  const onSetState = (value: boolean) => {
    // we check here if value is of type string. This means that we watch for one group only, and we don't have any
    // nested groups with select all checkboxes.
    if (typeof props.formFieldName === 'string') {
      // TODO we currently rely on internal state from formInstance, and we should change this to use array of
      // possible values passed as prop
      const formRefs = props.formInstance.control._fields[props.formFieldName] as FieldWithFields;
      let formValues: Array<string | boolean> | undefined = props.formInstance.getValues(props.formFieldName);
      const updatedValues: Array<string | boolean> = [];

      // form values can appear undefined if we unselected all profiles one by one, and clicked the select all. in that case
      // useForm has mounted the components, but value changes has not yet been applied. We can access current values via
      // internal refs, and we do that here and set selected values array (formValues) to what they hold as a value.
      // TODO this is temp fix, till we completely remove internal state dependency.
      if (formValues === undefined) {
        const filteredRefs: Record<number, Field> = Object.entries(formRefs).reduce((prev, [key, value]) => {
          if (typeof +key === 'number' && key !== '_f') {
            return { ...prev, [key]: value };
          }
          return prev;
        }, {});

        const mounted = Object.values(filteredRefs).reduce((prev, { _f }) => !!_f.mount, false);
        if (mounted) {
          formValues = Object.values(filteredRefs).map(({ _f }) => {
            const inputRefs = _f.refs;
            if (inputRefs?.length) {
              const inputRef = inputRefs[0] as HTMLInputElement;
              return inputRef.checked ? inputRef.value : false;
            }
            return false;
          });
        } else {
          formValues = [];
        }
      }

      formValues.forEach((item, index) => {
        const formRef = formRefs[index];
        if (formRef._f.refs) {
          const disabled = formRef._f.refs[0].disabled || formRef._f.refs[0].readOnly;
          const checkboxRefValue = formRef._f.refs[0].value;
          if (disabled) {
            updatedValues[index] = false;
          } else {
            updatedValues[index] = value ? checkboxRefValue ?? value : false;
          }
        }
      });
      props.formInstance.setValue(props.formFieldName, updatedValues);
    } else {
      // if formFieldName prop is an array it means we have a principal checkbox and nested groups.
      // we must provide formFieldNamePrincipal since it is required if formName is an array of group names
      if (!props.formFieldNamePrincipal) return;
      props.formFieldName.forEach(formName => {
        // TODO we currently rely on internal state from formInstance, and we should change this to use array of
        // possible values passed as prop
        const groupRefs = props.formInstance.control._fields[formName] as FieldWithFields;
        const groupValues: Array<string | boolean> = props.formInstance.getValues(formName);
        const updatedGroupValues: Array<string | boolean> = [];
        groupValues.forEach((item, index) => {
          const groupRef = groupRefs[index];
          if (groupRef._f.refs) {
            const disabled = groupRef._f.refs[0].disabled || groupRef._f.refs[0].readOnly;
            const checkboxRefValue = groupRef._f.refs[0].value;
            if (disabled) {
              updatedGroupValues[index] = false;
            } else {
              updatedGroupValues[index] = value ? checkboxRefValue ?? value : false;
            }
          }
        });
        props.formInstance.setValue(formName, updatedGroupValues);
      });
    }
  };

  const nameForCheckbox =
    typeof props.formFieldName === 'string' ? props.formFieldName : props.formFieldNamePrincipal || '';

  const watchCheckboxes: Array<string | boolean> | undefined = props.formInstance.watch(nameForCheckbox);
  const strigifiedWatchedCheckboxes = watchCheckboxes !== undefined ? JSON.stringify(watchCheckboxes) : '';

  useEffect(() => {
    if (watchCheckboxes?.length) {
      const filteredValues = watchCheckboxes.filter(
        value => typeof value === 'boolean' || (typeof value === 'string' && value)
      ); // we check against typeof since watchCheckboxes can have empty values in array that impacts array length. i.e. if we have disabled checkboxes
      const filterTrue = filteredValues.filter(value => value);
      const filterFalse = filteredValues.filter(value => !value);
      if (filteredValues.length && filterTrue.length === filteredValues.length) {
        setCheckboxState(true);
      } else if (!filterTrue.length) {
        setCheckboxState(false);
      } else if (filterFalse.length && filterTrue.length) {
        setCheckboxState('indeterminate');
      }
    }
    // We added strigifiedWatchedCheckboxes becase watch treats profiles array as the same instance, and does not fire this useEffect
    // we use plain watch over useWatch since we had issues with undefined value
  }, [strigifiedWatchedCheckboxes, nameForCheckbox, watchCheckboxes]);

  const viewProps: SelectAllCheckboxesViewProps = {
    ...props,
    onSetState,
    checkboxState
  };
  return <SelectAllCheckboxesView {...viewProps} />;
};
